import { PreparedTrade } from '../../../../models/intraday-chart.model';
import { SparklinePaths, SparklinePoint, SparklineSide } from './sparkline.model';

/** Market session start hour (local time) */
export const MARKET_OPEN_HOUR = 12;
/** Market session end hour (local time) */
export const MARKET_CLOSE_HOUR = 17;

/** Vertical padding as a fraction of price range */
export const RANGE_PADDING_RATIO = 0.1;
/** Minimum absolute vertical padding when range is non-zero */
export const MINIMUM_PRICE_PADDING = 1;
/** Vertical padding when all prices equal the baseline */
export const DEFAULT_PADDING = 1;

export function getMarketSessionBounds(
	nowMs: number,
	tradeTimes: readonly number[] = []
): { startMs: number; endMs: number } {
	const now = new Date(nowMs);
	const start = new Date(now);
	start.setHours(MARKET_OPEN_HOUR, 0, 0, 0);

	const marketClose = new Date(now);
	marketClose.setHours(MARKET_CLOSE_HOUR, 0, 0, 0);

	const startMs = start.getTime();
	const closeMs = marketClose.getTime();

	// Live end: clamp now into [open, close].
	let endMs = Math.min(Math.max(nowMs, startMs), closeMs);

	// Include the latest trade so the series can span the full chart width
	// (e.g. viewing before open, or demo data that ends before "now").
	if (tradeTimes.length) {
		const lastTradeMs = Math.max(...tradeTimes);
		endMs = Math.min(Math.max(endMs, lastTradeMs), closeMs);
	}

	if (endMs <= startMs) {
		endMs = closeMs;
	}

	return { startMs, endMs };
}

export function computeYDomain(prices: readonly number[], baseline: number): { yMin: number; yMax: number } {
	const minTrade = Math.min(...prices);
	const maxTrade = Math.max(...prices);
	const minValue = Math.min(minTrade, baseline);
	const maxValue = Math.max(maxTrade, baseline);
	const range = maxValue - minValue;

	if (range === 0) {
		const padding = Math.max(DEFAULT_PADDING, Math.abs(baseline) * 0.01 || DEFAULT_PADDING);
		return { yMin: baseline - padding, yMax: baseline + padding };
	}

	const padding = Math.max(range * RANGE_PADDING_RATIO, MINIMUM_PRICE_PADDING);
	return { yMin: minValue - padding, yMax: maxValue + padding };
}

export function sideOf(price: number, baseline: number): SparklineSide {
	if (price > baseline) {
		return 'positive';
	}
	if (price < baseline) {
		return 'negative';
	}
	return 'neutral';
}

export function mapTradesToPoints(
	trades: readonly PreparedTrade[],
	baseline: number,
	viewWidth: number,
	viewHeight: number,
	nowMs: number
): { points: SparklinePoint[]; baselineY: number } {
	if (!trades.length) {
		return { points: [], baselineY: viewHeight / 2 };
	}

	const { startMs, endMs } = getMarketSessionBounds(
		nowMs,
		trades.map(trade => trade.timeMs)
	);
	const xRange = Math.max(endMs - startMs, 1);
	const { yMin, yMax } = computeYDomain(
		trades.map(t => t.price),
		baseline
	);
	const yRange = yMax - yMin || 1;

	const toY = (price: number): number => viewHeight - ((price - yMin) / yRange) * viewHeight;
	const baselineY = toY(baseline);

	const points = trades.map(trade => {
		const clampedTime = Math.min(Math.max(trade.timeMs, startMs), endMs);
		return {
			x: ((clampedTime - startMs) / xRange) * viewWidth,
			y: toY(trade.price),
			price: trade.price,
			side: sideOf(trade.price, baseline)
		};
	});

	return { points, baselineY };
}

function interpolateCrossing(
	from: SparklinePoint,
	to: SparklinePoint,
	baseline: number,
	baselineY: number
): SparklinePoint {
	const priceDelta = to.price - from.price;
	const t = priceDelta === 0 ? 0 : (baseline - from.price) / priceDelta;

	return {
		x: from.x + t * (to.x - from.x),
		y: baselineY,
		price: baseline,
		side: 'neutral'
	};
}

/** Insert baseline intersection points wherever consecutive trades cross the baseline. */
export function expandWithCrossings(
	points: readonly SparklinePoint[],
	baseline: number,
	baselineY: number
): SparklinePoint[] {
	if (points.length < 2) {
		return [...points];
	}

	const expanded: SparklinePoint[] = [points[0]];

	for (let i = 1; i < points.length; i++) {
		const prev = points[i - 1];
		const curr = points[i];
		const crosses =
			(prev.price > baseline && curr.price < baseline) || (prev.price < baseline && curr.price > baseline);

		if (crosses) {
			expanded.push(interpolateCrossing(prev, curr, baseline, baselineY));
		}
		expanded.push(curr);
	}

	return expanded;
}

function buildLinePath(points: readonly SparklinePoint[]): string {
	if (!points.length) {
		return '';
	}

	return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

function buildFillPath(points: readonly SparklinePoint[], baselineY: number): string {
	if (points.length < 2) {
		return '';
	}

	const line = buildLinePath(points);
	const last = points[points.length - 1];
	const first = points[0];

	return `${line} L ${last.x} ${baselineY} L ${first.x} ${baselineY} Z`;
}

function segmentSide(a: SparklinePoint, b: SparklinePoint, baseline: number): SparklineSide {
	return sideOf((a.price + b.price) / 2, baseline);
}

/**
 * Splits the sparkline into positive / negative stroke and fill paths relative to
 * the baseline. Fill is always the area between the price line and the baseline.
 */
export function buildSparklinePaths(
	points: readonly SparklinePoint[],
	baseline: number,
	baselineY: number
): SparklinePaths {
	const empty: SparklinePaths = {
		positiveFill: '',
		negativeFill: '',
		positiveStroke: '',
		negativeStroke: '',
		neutralStroke: '',
		baselineY
	};

	if (!points.length) {
		return empty;
	}

	const expanded = expandWithCrossings(points, baseline, baselineY);

	if (expanded.length === 1) {
		const [point] = expanded;
		const stroke = `M ${point.x} ${point.y} L ${point.x} ${point.y}`;
		return {
			...empty,
			positiveStroke: point.side === 'positive' ? stroke : '',
			negativeStroke: point.side === 'negative' ? stroke : '',
			neutralStroke: point.side === 'neutral' ? stroke : ''
		};
	}

	const positiveFill: string[] = [];
	const negativeFill: string[] = [];
	const positiveStroke: string[] = [];
	const negativeStroke: string[] = [];
	const neutralStroke: string[] = [];

	let run: SparklinePoint[] = [expanded[0]];
	let runSide = segmentSide(expanded[0], expanded[1], baseline);

	const flush = (): void => {
		if (run.length < 2) {
			return;
		}

		const line = buildLinePath(run);
		if (runSide === 'positive') {
			positiveStroke.push(line);
			positiveFill.push(buildFillPath(run, baselineY));
		} else if (runSide === 'negative') {
			negativeStroke.push(line);
			negativeFill.push(buildFillPath(run, baselineY));
		} else {
			neutralStroke.push(line);
		}
	};

	for (let i = 1; i < expanded.length; i++) {
		const prev = expanded[i - 1];
		const curr = expanded[i];
		const side = segmentSide(prev, curr, baseline);

		if (side !== runSide) {
			flush();
			run = [prev, curr];
			runSide = side;
			continue;
		}

		run.push(curr);
	}

	flush();

	return {
		positiveFill: positiveFill.filter(Boolean).join(' '),
		negativeFill: negativeFill.filter(Boolean).join(' '),
		positiveStroke: positiveStroke.filter(Boolean).join(' '),
		negativeStroke: negativeStroke.filter(Boolean).join(' '),
		neutralStroke: neutralStroke.filter(Boolean).join(' '),
		baselineY
	};
}
